import cx from 'classnames'
import { Formik } from 'formik'

import Styles from './Review.module.scss'

import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { useIsMutating } from 'hooks/useIsMutating'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

import { Carousel } from 'components/Carousel/Carousel'
import { Form } from '../../../Form/Form'
import { Button } from 'blaze-ui/Button'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { ArrowRefresh2, Edit1, Trash4 } from 'blaze-ui/icons'
import { Skeleton } from 'antd'
import { DeclarativeToast } from 'common/ToastMessage/DeclarativeToast'
import { ToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AlmanacFileSummary } from 'types'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { DESIGN_PATH, FOLDER_PATH } from 'paths'
import { getAlmanacUrlFor } from 'utils/urlUtils'
import { generatePath } from 'react-router-dom'
import { Design as DesignType } from '../../RecommendMoreContentWizard'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { UserProfileCircle } from 'blaze-ui/icons/32/UserProfileCircle'
import { LoadingShimmerer } from '../../../../LoadingShimmerer/LoadingShimmerer'

const Design = ({
  design,
  externalIntegration,
  onRegenerate,
  onDelete,
}: {
  design?: AlmanacFileSummary
  externalIntegration?: ExternalIntegration
  onRegenerate: () => void
  onDelete: () => void
}) => {
  const isLoadingPreview = !design?.preview_url
  const openWindowSafely = useOpenWindowSafely()
  const { isMobile } = useResponsiveDesign()
  const isLoadingNewVersion =
    !!design?.last_rendered_version && !!design?.version && design.last_rendered_version < design.version

  const editDesign = () => {
    if (!design) return

    openWindowSafely(getAlmanacUrlFor(generatePath(DESIGN_PATH, { key: design?.id })))
  }

  return (
    <div className={Styles.designContainer}>
      <div className={Styles.design}>
        <div className={Styles.designHeader}>
          <div className={Styles.designAvatar}>
            {externalIntegration?.picture ? (
              <img src={externalIntegration?.picture} alt="external integration" />
            ) : (
              <UserProfileCircle size={20} />
            )}
          </div>
          <span className={Styles.accountName}>{externalIntegration?.name}</span>
        </div>
        <div className={Styles.photoContainer}>
          <LoadingShimmerer
            show={!design?.preview_url || isLoadingNewVersion}
            className={cx(Styles.shimmerer, { [Styles.loading]: isLoadingNewVersion })}
            borderRadius={0}
          >
            {design?.preview_url ? (
              <div className={Styles.photo}>
                <img src={design.preview_url} alt={design.title} />
              </div>
            ) : (
              <div data-testid="shimmer" className={Styles.loadingPhoto} />
            )}
          </LoadingShimmerer>
        </div>
        {!design?.captions?.[0] ? (
          <div className={Styles.loadingDesignFooter}>
            <div className={Styles.loadingAccountName}>
              <span className={Styles.accountName}>{externalIntegration?.name}</span>
              <Skeleton active paragraph={false} />
            </div>
            <Skeleton active paragraph={false} />
          </div>
        ) : (
          <div className={Styles.designFooter}>
            <span className={Styles.caption}>
              <span className={Styles.accountName}>{externalIntegration?.name} </span>
              <span data-testid={design.captions[0]}>{design.captions[0]}</span>
            </span>
          </div>
        )}
      </div>
      <div className={Styles.designControls}>
        <Button onPress={editDesign} frontIcon={Edit1} variant="secondary" size="sm" isDisabled={isLoadingPreview}>
          {!isMobile && 'Edit'}
        </Button>
        <Button
          onPress={onRegenerate}
          frontIcon={ArrowRefresh2}
          variant="secondary"
          size="sm"
          isDisabled={isLoadingPreview}
        >
          {!isMobile && 'Regenerate'}
        </Button>
        <IconButton onPress={onDelete} icon={Trash4} variant="secondary" size="sm" isDisabled={isLoadingPreview} />
      </div>
    </div>
  )
}

type Props = StepProps & {
  designs: (DesignType | undefined)[]
  externalIntegration: ExternalIntegration
  regenerate: (index: number) => void
  deleteDesign: (index: number) => void
  deleteDesigns: () => void
}

export const Review = multiStep(
  ({ schema, onSubmit, onBack, designs, externalIntegration, regenerate, deleteDesign, deleteDesigns }: Props) => {
    const { meta } = schema.describe()
    const creatingDesign = useIsMutating({ mutationKey: 'createDesign' })
    const fetchingDesignPreviews = useIsMutating({ mutationKey: 'fetchDesignPreview' })
    const hasUndefinedDesigns = designs.some((d) => d === undefined)
    const openWindowSafely = useOpenWindowSafely()
    const { isMobile } = useResponsiveDesign()

    const isLoading = hasUndefinedDesigns && (creatingDesign !== 0 || fetchingDesignPreviews !== 0)
    const folderSlug = designs[0]?.parent?.seo_string

    const openFolder = () => {
      openWindowSafely(getAlmanacUrlFor(generatePath(FOLDER_PATH, { folderSlug })))
    }

    return (
      <>
        {isLoading && (
          <DeclarativeToast>
            <ToastMessage toastMessage="Generating posts..." variant="generating" />
          </DeclarativeToast>
        )}
        <Formik
          validationSchema={schema}
          onSubmit={onSubmit}
          initialValues={schema.cast({ ...schema.getDefault() }, { assert: false })}
        >
          {({ values, submitForm }) => (
            <Form.Root>
              <Form.Section>
                <div className={Styles.container}>
                  <div className={Styles.header}>
                    <Heading level={4}>Your content is ready</Heading>
                    <Text variant="secondary">Edit posts as needed, then schedule them on your content calendar.</Text>
                  </div>
                  <div className={Styles.body}>
                    {!isMobile ? (
                      <Carousel offset={0} withControls={false}>
                        {designs.map((design, i) => {
                          const key = `design-${i}`

                          return (
                            <Design
                              key={key}
                              design={design}
                              externalIntegration={externalIntegration}
                              onRegenerate={() => regenerate(i)}
                              onDelete={() => deleteDesign(i)}
                            />
                          )
                        })}
                      </Carousel>
                    ) : (
                      <div className={Styles.mobileDesignsContainer}>
                        {designs.map((design, i) => {
                          const key = `design-${i}`

                          return (
                            <Design
                              key={key}
                              design={design}
                              externalIntegration={externalIntegration}
                              onRegenerate={() => regenerate(i)}
                              onDelete={() => deleteDesign(i)}
                            />
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </Form.Section>
              <Form.Actions isSticky>
                {!isMobile && (
                  <Form.PreviousButton
                    onPress={() => {
                      deleteDesigns()
                      onBack?.()
                    }}
                  >
                    {meta.previousLabel}
                  </Form.PreviousButton>
                )}
                <Form.PrimaryActions>
                  <div className={Styles.buttonGroup}>
                    <Button onPress={openFolder} isDisabled={!folderSlug} variant="secondary">
                      Open The Project
                    </Button>
                    <Button onPress={submitForm} isDisabled={isLoading}>
                      {meta.submitLabel}
                    </Button>
                  </div>
                </Form.PrimaryActions>
              </Form.Actions>
            </Form.Root>
          )}
        </Formik>
      </>
    )
  },
)
